import { useRef, useState } from 'react';
import type { CsvResult, TimetableDay } from '@noorview/core';
import { importTimetableCsv, timetableTemplateCsv } from '@noorview/core';
import { attempt, backend } from '../data/store';
import { Sheet } from '../ui/controls';
import { Icon } from '../ui/icons';

export function download(name: string, text: string, type = 'text/csv') {
  const url = URL.createObjectURL(new Blob(['﻿' + text], { type: `${type};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Twelve months of blank rows, from the first of this month. */
function templateText() {
  const d = new Date();
  const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const end = new Date(d.getFullYear() + 1, d.getMonth(), 1);
  const days = Math.round((end.getTime() - new Date(d.getFullYear(), d.getMonth(), 1).getTime()) / 86_400_000);
  return timetableTemplateCsv(from, days);
}

export function CsvSheet({ onClose }: { onClose: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState('');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const res: CsvResult | null = text ? importTimetableCsv(text) : null;

  const read = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { setText(''); setFile('That file is too large for a timetable (over 2 MB).'); return; }
    setFile(f.name);
    setText(await f.text());
  };
  const submit = async () => {
    if (!res?.days.length) return;
    setBusy(true);
    // Each day in the file replaces that day's times; days not in the file are kept.
    const ok = await attempt(() => backend.setTimetable(res.days, 'days'), `${res.days.length} days imported`);
    setBusy(false);
    if (ok !== undefined) onClose();
  };
  const has = (k: keyof TimetableDay) => !!res?.days.some((d) => d[k]);

  return (
    <Sheet open onClose={onClose} title="Upload timetable" subtitle="Your screens take every prayer time from this file." size="wide"
      footer={<><button type="button" className="btn plain" onClick={onClose}>Cancel</button><button type="button" className="btn primary" disabled={busy || !res?.days.length} onClick={submit}>{busy ? 'Importing…' : res?.days.length ? `Import ${res.days.length} days` : 'Import'}</button></>}>
      <div className="csv-cards">
        <button type="button" className={`drop${over ? ' over' : ''}`} onClick={() => input.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); void read(e.dataTransfer.files[0]); }}>
          <Icon name="upload" size={30} />
          <b>{file || 'Choose CSV file'}</b>
          <small className="hint">{file ? 'Choose a different file' : 'Or drop it here'}</small>
        </button>
        <button type="button" className="drop alt" onClick={() => download('noorview-timetable-template.csv', templateText())}>
          <Icon name="download" size={30} />
          <b>Download template</b>
          <small className="hint">Every date for the next 12 months, ready to fill in</small>
        </button>
      </div>
      <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => { void read(e.target.files?.[0]); e.target.value = ''; }} />
      {!res && (
        <ul className="csv-notes">
          <li><b>Columns:</b> Gregorian Date, Hijri Date, Sunrise, then Begins and Jamaat for Fajr, Zuhr, Asr, Maghrib and Isha, then Jumuah 1, Jumuah 2, Sehri Ends and Tarawih.</li>
          <li><b>Hijri Date can be left blank.</b> NoorView works it out automatically.</li>
          <li><b>All times in 24-hour format</b>, e.g. 05:30 and 19:45. Times like 7:45 pm are converted too.</li>
          <li><b>Sehri Ends and Maghrib Jamaat fill themselves in:</b> Sehri 10 minutes before Fajr begins, Maghrib Jamaat at Maghrib begins. Type over them to use your own times.</li>
          <li><b>Tarawih</b> can stay blank until Ramadan. Fill it in then and upload again.</li>
          <li>Jumuah 2 is only needed if you hold a second Jumu‘ah.</li>
        </ul>
      )}
      {res && (
        <div className="stack">
          {res.errors.map((e) => <div key={e} className="banner bad">{e}</div>)}
          {res.warnings.slice(0, 4).map((w) => <div key={w} className="banner">{w}</div>)}
          {res.days.length > 0 && (<>
            <div className="hint">Found {res.days.length} days ({res.days[0]!.date} to {res.days.at(-1)!.date}).{has('tarawih') || has('sehri') ? ' Includes Ramadan times.' : ''}{has('jumuah1') ? ' Includes Jumu‘ah times.' : ''}</div>
            <div className="table-wrap" style={{ maxHeight: 240 }}>
              <table className="tt"><thead><tr><th>Date</th><th>Fajr</th><th>Jamaat</th><th>Sunrise</th><th>Zuhr</th><th>Jamaat</th><th>Asr</th><th>Jamaat</th><th>Maghrib</th><th>Jamaat</th><th>Isha</th><th>Jamaat</th>{has('jumuah1') && <th>Jumuah</th>}{has('tarawih') && <th>Tarawih</th>}</tr></thead>
                <tbody>{res.days.slice(0, 8).map((d) => <tr key={d.date}><th>{d.date}</th><td>{d.fajr ?? '—'}</td><td>{d.fajrJamaah ?? '—'}</td><td>{d.sunrise ?? '—'}</td><td>{d.dhuhr ?? '—'}</td><td>{d.dhuhrJamaah ?? '—'}</td><td>{d.asr ?? '—'}</td><td>{d.asrJamaah ?? '—'}</td><td>{d.maghrib ?? '—'}</td><td>{d.maghribJamaah ?? '—'}</td><td>{d.isha ?? '—'}</td><td>{d.ishaJamaah ?? '—'}</td>{has('jumuah1') && <td>{[d.jumuah1, d.jumuah2].filter(Boolean).join(' · ') || '—'}</td>}{has('tarawih') && <td>{d.tarawih ?? '—'}</td>}</tr>)}</tbody></table>
            </div>
          </>)}
        </div>
      )}
    </Sheet>
  );
}
